import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, UserPlus, ShieldCheck, ShieldOff, Ban, CheckCircle2 } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SACard, timeAgo } from "@/components/superadmin/SAPrimitives";

function InviteModal({ onClose, onInvited }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("admin");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await http.post("/superadmin/team/invite", { email, name, role });
            setResult(data);
            onInvited?.();
        } catch (e2) {
            setErr(errMsg(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <SACard className="w-full max-w-md" data-testid="sa-team-invite-modal">
                {!result ? (
                    <form onSubmit={submit} className="space-y-3">
                        <h2 className="text-[15px] font-semibold text-white">Add platform team member</h2>
                        <div>
                            <label className="mb-1 block text-[11px] text-white/55">Email</label>
                            <input
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="sa-team-invite-email"
                                className="h-9 w-full rounded-md border border-white/10 bg-white/[0.03] px-3 text-[13px] text-white focus:border-[#00C2FF]/50 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] text-white/55">Name (optional)</label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid="sa-team-invite-name"
                                className="h-9 w-full rounded-md border border-white/10 bg-white/[0.03] px-3 text-[13px] text-white focus:border-[#00C2FF]/50 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] text-white/55">Role</label>
                            <select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                data-testid="sa-team-invite-role"
                                className="h-9 w-full rounded-md border border-white/10 bg-white/[0.03] px-3 text-[13px] text-white focus:outline-none"
                            >
                                <option value="admin">Admin (no console access)</option>
                                <option value="superadmin">Superadmin (full console access)</option>
                            </select>
                        </div>
                        {err && <div className="text-[12px] text-red-300">{err}</div>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-md border border-white/10 px-3 py-1.5 text-[12.5px] text-white/70 hover:bg-white/5"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="sa-team-invite-submit"
                                className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-50"
                                style={{ background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)" }}
                            >
                                {busy ? "Adding…" : "Add member"}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="space-y-3" data-testid="sa-team-invite-result">
                        <h2 className="text-[15px] font-semibold text-white">Member added</h2>
                        <p className="text-[12.5px] text-white/70">
                            Email delivery:{" "}
                            <span className={result.email_delivery === "sent" ? "text-emerald-300" : "text-amber-300"}>
                                {result.email_delivery}
                            </span>
                        </p>
                        {result.email_delivery !== "sent" && (
                            <div className="rounded-md border border-amber-400/30 bg-amber-400/10 p-3 text-[12px] text-amber-200">
                                Email wasn't sent — share this temporary password with{" "}
                                {email} securely. It won't be shown again.
                                <div className="mt-2 select-all rounded bg-black/30 px-2 py-1 font-mono text-[12px] text-white">
                                    {result.temp_password}
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end pt-2">
                            <button
                                onClick={onClose}
                                className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold text-white"
                                style={{ background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)" }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </SACard>
        </div>
    );
}

export default function SuperAdminSettings() {
    const { user: me } = useAuth();
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [showInvite, setShowInvite] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/superadmin/team");
            setTeam(data || []);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const changeRole = async (u, role) => {
        setBusyId(u.id);
        try {
            await http.patch(`/superadmin/team/${u.id}/role`, { role });
            await load();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    const toggleDisabled = async (u) => {
        setBusyId(u.id);
        try {
            await http.post(`/superadmin/team/${u.id}/${u.disabled ? "enable" : "disable"}`);
            await load();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-5" data-testid="sa-settings-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Settings</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        Platform team &middot; {team.length} member{team.length === 1 ? "" : "s"}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="sa-settings-refresh"
                        className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button
                        onClick={() => setShowInvite(true)}
                        data-testid="sa-settings-invite"
                        className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold text-white"
                        style={{ background: "linear-gradient(135deg, #004AAD 0%, #00C2FF 100%)" }}
                    >
                        <UserPlus size={12} /> Add member
                    </button>
                </div>
            </div>

            {err && <SACard className="text-red-300">{err}</SACard>}

            <SACard className="p-0">
                <div className="border-b border-white/5 px-5 py-3 text-[13px] font-semibold text-white">
                    Superadmin &amp; platform-admin accounts
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Name</th>
                                <th className="px-2 py-2.5 font-medium">Email</th>
                                <th className="px-2 py-2.5 font-medium">Role</th>
                                <th className="px-2 py-2.5 font-medium">Status</th>
                                <th className="px-2 py-2.5 font-medium">Added</th>
                                <th className="px-5 py-2.5 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {team.map((u) => {
                                const isSelf = u.id === me?.id;
                                return (
                                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`sa-team-row-${u.id}`}>
                                        <td className="px-5 py-2.5 text-white/85">
                                            {u.name || "—"}{isSelf && <span className="ml-1.5 text-[10px] text-white/40">(you)</span>}
                                        </td>
                                        <td className="px-2 py-2.5 text-white/70">{u.email}</td>
                                        <td className="px-2 py-2.5">
                                            <span
                                                className={
                                                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                                                    (u.role === "superadmin"
                                                        ? "bg-[#00C2FF]/10 text-[#00C2FF]"
                                                        : "bg-white/10 text-white/70")
                                                }
                                            >
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-2 py-2.5">
                                            {u.disabled ? (
                                                <span className="text-red-300">Disabled</span>
                                            ) : (
                                                <span className="text-emerald-300">Active</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-2.5 text-white/55">{timeAgo(u.created_at)}</td>
                                        <td className="px-5 py-2.5">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {!isSelf && (
                                                    <button
                                                        disabled={busyId === u.id}
                                                        onClick={() => changeRole(u, u.role === "superadmin" ? "admin" : "superadmin")}
                                                        data-testid={`sa-team-role-toggle-${u.id}`}
                                                        title={u.role === "superadmin" ? "Demote to admin" : "Promote to superadmin"}
                                                        className="rounded-md p-1.5 text-white/60 hover:bg-white/5 hover:text-white disabled:opacity-40"
                                                    >
                                                        {u.role === "superadmin" ? <ShieldOff size={13} /> : <ShieldCheck size={13} />}
                                                    </button>
                                                )}
                                                {!isSelf && (
                                                    <button
                                                        disabled={busyId === u.id}
                                                        onClick={() => toggleDisabled(u)}
                                                        data-testid={`sa-team-disable-toggle-${u.id}`}
                                                        title={u.disabled ? "Re-enable account" : "Disable account"}
                                                        className="rounded-md p-1.5 text-white/60 hover:bg-white/5 hover:text-white disabled:opacity-40"
                                                    >
                                                        {u.disabled ? <CheckCircle2 size={13} /> : <Ban size={13} />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!loading && team.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                                        No platform team members yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>

            {showInvite && (
                <InviteModal
                    onClose={() => { setShowInvite(false); load(); }}
                    onInvited={load}
                />
            )}
        </div>
    );
}
